import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type { SwoogoProjectConfigurationRequest, UserDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type SwoogoFormValues = {
  enabled: boolean;
  clientId: string;
  clientSecret: string;
  creatorUser: UserDto | undefined;
};

export function SwoogoPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();
  const userQueries = useUserQueries();

  const {
    data: config,
    isFetching: isLoadingConfig,
    error: configError,
  } = useQuery(integrationQueries.swoogoConfigurations());
  const {
    data: adminUsers,
    isFetching: isLoadingAdminUsers,
    error: adminUsersError,
  } = useQuery(userQueries.listAdmins());
  const mutation = integrationMutations.useEditSwoogoConfigurations();

  if (!hasPermission((x) => x.integrations.canManageSwoogo)) {
    return <ErrorPage status={404} />;
  }

  if (configError || adminUsersError) {
    return <ErrorPage error={configError || adminUsersError} />;
  }

  if (isLoadingConfig || isLoadingAdminUsers) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.swoogo-configurations.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <SwoogoForm
        defaultValues={{
          enabled: config?.enabled || false,
          clientId: config?.clientId || "",
          clientSecret: config?.clientSecret || "",
          creatorUser: config?.creatorUser || adminUsers?.[0],
        }}
        adminUsers={adminUsers || []}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

type SwoogoFormProps = {
  defaultValues: SwoogoFormValues;
  adminUsers: UserDto[];
  isSubmitting: boolean;
  onSubmit: (data: SwoogoProjectConfigurationRequest) => void;
};

function SwoogoForm({ adminUsers, defaultValues, isSubmitting, onSubmit }: SwoogoFormProps): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<SwoogoFormValues>({ defaultValues });

  const currEnabled = useWatch({ name: "enabled", control: formMethods.control });

  const handleSubmit = (formValues: SwoogoFormValues) => {
    onSubmit({
      enabled: formValues.enabled,
      clientId: formValues.clientId,
      clientSecret: formValues.clientSecret,
      creatorUserId: formValues.creatorUser?.id,
    });
  };

  return (
    <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
      <Form.Container title={t("page.integration.swoogo-configurations.title")}>
        <Form.Body>
          <FormCheckbox<SwoogoFormValues, "enabled"> id="enabled" name="enabled" label={t("common.action.enable")} />
          <FormField label={t("page.integration.swoogo.form.client-id")} required>
            <FormInput<SwoogoFormValues, "clientId">
              id="clientId"
              name="clientId"
              rules={{
                validate: {
                  required: (value, formValues) => {
                    if (formValues.enabled) {
                      return createRequiredStringRule(t, "page.integration.swoogo.form.client-id")(value, formValues);
                    }
                  },
                },
              }}
              disabled={isSubmitting || !currEnabled}
            />
          </FormField>
          <FormField label={t("page.integration.swoogo.form.client-secret")} required>
            <FormInput<SwoogoFormValues, "clientSecret">
              id="clientSecret"
              name="clientSecret"
              type="password"
              rules={{
                validate: {
                  required: (value, formValues) => {
                    if (formValues.enabled) {
                      return createRequiredStringRule(t, "page.integration.swoogo.form.client-secret")(
                        value,
                        formValues,
                      );
                    }
                  },
                },
              }}
              disabled={isSubmitting || !currEnabled}
            />
          </FormField>
          <FormField label={t("page.integration.swoogo.form.event-creator-user")} required>
            <div className="max-w-search-wide">
              <FormSelect<SwoogoFormValues, UserDto>
                items={adminUsers || []}
                keySelector={(user) => user.id}
                renderOption={(user) => `${user.firstName} ${user.lastName}`}
                name="creatorUser"
                rules={{
                  validate: {
                    required: (value, formValues) => {
                      if (formValues.enabled && !value) {
                        return t("components.form.error.required", {
                          inputName: t("page.integration.swoogo.form.event-creator-user"),
                        });
                      }
                    },
                  },
                }}
                disabled={isSubmitting || !currEnabled}
              />
            </div>
          </FormField>
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
